import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * Field composition primitives for Input / Textarea — from the Figma DS2026
 * "Input field" + "Text area" specs (First Bite Design System, nodes
 * 1536:18108 / 7148:6098).
 *
 *   <Field status="error">
 *     <FieldLabel htmlFor="email">Email</FieldLabel>
 *     <Input id="email" placeholder="info@gmail.com" />
 *     <FieldHint>This is a hint text to help user.</FieldHint>
 *   </Field>
 *
 * `Field` publishes its `status` through context so the enclosed `Input` /
 * `Textarea` border + ring and the `FieldHint` text recolor together
 * (default → gray, error → red, success → green) — matching the three Figma
 * columns. A `status` prop set directly on `Input`/`Textarea` always wins over
 * the context, so the controls also work standalone (without a `Field`).
 */

export type FieldStatus = 'default' | 'error' | 'success'

const FieldStatusContext = React.createContext<FieldStatus | undefined>(
  undefined
)

/** Resolve the effective status: explicit prop > Field context > 'default'. */
export function useFieldStatus(status?: FieldStatus): FieldStatus {
  const ctx = React.useContext(FieldStatusContext)
  return status ?? ctx ?? 'default'
}

const FieldDisabledContext = React.createContext<boolean>(false)

/** Resolve the effective disabled state: explicit prop > Field context > false. */
export function useFieldDisabled(disabled?: boolean): boolean {
  const ctx = React.useContext(FieldDisabledContext)
  return disabled ?? ctx
}

/**
 * Resting + focus border and the translucent 4px focus ring, per status — the
 * Figma "Brand/Error/Success focus border color" + "Focus Rings/*" tokens.
 * Shared by `inputVariants` and `textareaVariants` so the controls stay in step.
 */
export const fieldControlStatus = {
  default:
    'border-fbui-gray-300 focus:border-fbui-basil-700 focus:ring-fbui-ring-brand',
  error:
    'border-fbui-red-500 focus:border-fbui-red-500 focus:ring-fbui-ring-error',
  success:
    'border-fbui-green-500 focus:border-fbui-green-500 focus:ring-fbui-ring-success',
} as const satisfies Record<FieldStatus, string>

/**
 * The same per-status border + ring, but keyed off `focus-within` — for shells
 * that wrap a borderless control plus addons (e.g. `InputGroup` / `SearchBar`),
 * so the whole row lights up when the inner input is focused.
 */
export const fieldGroupStatus = {
  default:
    'border-fbui-gray-300 focus-within:border-fbui-basil-700 focus-within:ring-fbui-ring-brand',
  error:
    'border-fbui-red-500 focus-within:border-fbui-red-500 focus-within:ring-fbui-ring-error',
  success:
    'border-fbui-green-500 focus-within:border-fbui-green-500 focus-within:ring-fbui-ring-success',
} as const satisfies Record<FieldStatus, string>

/** Help-text color, per status (gray / red / green) — the Figma hint rows. */
export const fieldHintColor = {
  default: 'text-fbui-gray-500',
  error: 'text-fbui-red-600',
  success: 'text-fbui-green-600',
} as const satisfies Record<FieldStatus, string>

export interface FieldProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Status shared with the enclosed control + hint. Defaults to 'default'. */
  status?: FieldStatus
  /**
   * Grays the `FieldLabel` + `FieldHint` to their disabled treatment. Set the
   * enclosed control's own `disabled` too — controls manage that themselves.
   */
  disabled?: boolean
}

/** Vertical stack (label · control · hint) that publishes `status` + `disabled`. */
export const Field = React.forwardRef<HTMLDivElement, FieldProps>(
  ({ className, status = 'default', disabled = false, children, ...props }, ref) => (
    <FieldStatusContext.Provider value={status}>
      <FieldDisabledContext.Provider value={disabled}>
        <div
          ref={ref}
          className={cn('flex w-full flex-col gap-1.5', className)}
          {...props}
        >
          {children}
        </div>
      </FieldDisabledContext.Provider>
    </FieldStatusContext.Provider>
  )
)
Field.displayName = 'Field'

export interface FieldLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  /** Append a muted "(optional)" suffix after the label text. */
  optional?: boolean
  /** Gray to the disabled treatment. Inherited from a parent `<Field>` when omitted. */
  disabled?: boolean
}

/** Form label — 14px medium gray-700, grays out when the field is disabled. */
export const FieldLabel = React.forwardRef<HTMLLabelElement, FieldLabelProps>(
  ({ className, optional, disabled, children, ...props }, ref) => {
    const isDisabled = useFieldDisabled(disabled)
    return (
      <label
        ref={ref}
        className={cn(
          'text-sm font-medium tracking-[-0.2px] select-none',
          isDisabled ? 'text-fbui-text-disabled' : 'text-fbui-gray-700',
          className
        )}
        {...props}
      >
        {children}
        {optional && (
          <span
            className={cn(
              'ml-1 font-normal',
              isDisabled ? 'text-fbui-text-disabled' : 'text-fbui-gray-500'
            )}
          >
            (optional)
          </span>
        )}
      </label>
    )
  }
)
FieldLabel.displayName = 'FieldLabel'

export interface FieldHintProps extends React.HTMLAttributes<HTMLParagraphElement> {
  /** Override the inherited Field status (e.g. for a standalone hint). */
  status?: FieldStatus
  /** Gray to the disabled treatment. Inherited from a parent `<Field>` when omitted. */
  disabled?: boolean
}

/** Help text under the control — 12px, recolored to match the field status. */
export const FieldHint = React.forwardRef<HTMLParagraphElement, FieldHintProps>(
  ({ className, status, disabled, children, ...props }, ref) => {
    const resolved = useFieldStatus(status)
    const isDisabled = useFieldDisabled(disabled)
    if (!children) return null
    return (
      <p
        ref={ref}
        className={cn(
          'text-xs tracking-[-0.2px]',
          isDisabled ? 'text-fbui-text-disabled' : fieldHintColor[resolved],
          className
        )}
        {...props}
      >
        {children}
      </p>
    )
  }
)
FieldHint.displayName = 'FieldHint'
